import gsap from 'gsap';
import { BONUS_AMOUNT_MULTIPLE, FONT_BALOO_NAME, FONT_LATO_NAME, GAME_SCENE } from '../../../core/constats';
import { mobileBackgroundRssNames } from '../../../core/mobileResourceNames';
import { DrawerBase } from '../../../core/abstraction/DrawerBase';
import { buyBonusRssNames } from '../../../core/gameConstants';
import { getTranslation, translationKeys } from '../../../core/translation/translation';
import { gameMobileConfig } from '../../../core/configs';
import { amountFormatter } from '../../../core/helpers/amountFormatter';
import { autoSpinHelper } from '../../../services/helperService/AutoSpinHelper';
import { partnerService } from '../../../services/helperService/PartnerService';
import { popupHelper } from '../../../services/helperService/PopupHelper';
import {
  BONUS_COST_AMOUNT,
  GAME_SCENE_STATE,
  PRE_BONUS_ID,
  SPIN_AMOUNT,
  SPIN_AMOUNT_ACTIVE,
} from '../../../stateManagment/gameState';
import { soundService } from '../../../services/helperService/PixiSoundService';
import { debounce } from '../../../core/helpers/debounce';
import { buyBonusButtonHelper } from '../../../services/helperService/BuyBonusButtonHelper';
import { gameService } from '../../../services/GameService';
import { userService } from '../../../services/helperService/UserService';
import { notificationHandler } from '../../../services/NotificationHandler';
import {spinButtonHelper} from "../../../services/helperService/SpinButtonHelper";
import {gameDrawer} from "../../../game/GameDrawer";

export class BaseBuyBonusPopup extends DrawerBase {

  configButtonsImage(buttons, { scale }) {
    buttons.forEach(button => {
      button.scale.set(scale);
      button.anchor.set(.5, 1);
    });
  }

  setupButtonEvents({ parent, hoverIcon, normalIcon }) {
    parent
      .on('pointerover', () => {
        hoverIcon.visible = true;
        normalIcon.visible = false;
      })
      .on('pointerdown', () => {
        hoverIcon.visible = true;
        normalIcon.visible = false;
      })
      .on('pointerout', () => {
        hoverIcon.visible = false;
        normalIcon.visible = true;
      })
      .on('pointerup', () => {
        hoverIcon.visible = false;
        normalIcon.visible = true;
      });
  }

  onClick = () => debounce(() => {
    spinButtonHelper.disable()
    buyBonusButtonHelper.disable();
    if(!gameService.isRealMode && !userService.checkBalance(BONUS_COST_AMOUNT.getValue())){
      notificationHandler.notEnoughMoney()
      buyBonusButtonHelper.enable();
      spinButtonHelper.enable()
      return
    }
    gameService.buyBonus().then(data => {
      userService.updateBalanceBuyBonusData(data)
      PRE_BONUS_ID.next(data);
      GAME_SCENE_STATE.next(GAME_SCENE.PRE_BONUS);
    }).catch(() => {
      spinButtonHelper.enable()
      buyBonusButtonHelper.enable();
    });
  }, 300, true);

  getPopup({
    sizes: {
      width = gameMobileConfig.width,
      parentWidth = gameMobileConfig.config.width,
      parentHeight = gameMobileConfig.config.height,
      actionWidth = 160,
      actionHeight = 140,
      padding = gameMobileConfig.width * .25,
      percent = 20,
      scale = 1,
    } = {},
    onClick = () => null,
  } = {}) {
    /** create containers */
    const container = this.createContainer('BuyBonusMobilePopup');
    const popupContainer = this.createContainer('popupContainer');
    const declineContainer = this.createContainer('declineContainer');
    const acceptContainer = this.createContainer('acceptContainer');
    container.visible = popupHelper.buyBonusPopupState;

    /** calculate space for button */
    const spaceForButton = (parentWidth - width) / 2;

    /** config black rect*/
    const blackRect = this.createGraphics()
      .beginFill(0x000000)
      .drawRect(
        0,
        0,
        parentWidth,
        parentHeight,
      )
      .endFill();
    blackRect.alpha = .9;

    /** setup top shadow */
    const shadow = this.createSprite(this.resources[mobileBackgroundRssNames.shadow]);
    shadow.width = parentWidth;
    shadow.rotation = Math.PI;
    shadow.anchor.set(.5);
    this.setPosition(shadow, { x: parentWidth / 2, y: shadow.height / 2 });

    /** setup open and close state */
    this.addSubscriber(popupHelper.subscribeBuyBonusPopup(isOpen => {
      if (isOpen) {
        container.visible = isOpen;
        this.setPosition(popupContainer, { y: -900 });
        gsap.to(blackRect, { duration: 1.5, ease: 'power1.out', alpha: .7 });
        gsap.to(shadow, { duration: 1.5, ease: 'power1.out', alpha: .7 });
        gsap.to(popupContainer, { duration: 1.5, ease: 'elastic.out(.2, .1)', y: 0 });
      } else {
        gsap.to(blackRect, { duration: .5, ease: 'power1.in', alpha: 0 });
        gsap.to(shadow, { duration: .5, ease: 'power1.in', alpha: 0 });
        gsap.to(popupContainer, {
          duration: .5, ease: 'power1.in', y: -800, onComplete: () => {
            container.visible = isOpen;
          },
        });
      }
    }, true));

    /** Create popup image and buttons texture */
    const popupBuyBonus = this.createSprite(this.resources[buyBonusRssNames.popupBuyBonus]);
    const acceptHover = this.createSprite(this.resources[buyBonusRssNames.acceptHover]);
    const acceptNormal = this.createSprite(this.resources[buyBonusRssNames.acceptNormal]);
    const declineHover = this.createSprite(this.resources[buyBonusRssNames.declineHover]);
    const declineNormal = this.createSprite(this.resources[buyBonusRssNames.declineNormal]);

    /** hide hovers state */
    acceptHover.visible = false;
    declineHover.visible = false;

    /**Set popup image configs(positions size)*/
    popupBuyBonus.scale.set(width / popupBuyBonus.width);
    popupBuyBonus.anchor.set(.5, 1);
    const popupPositionY = parentHeight / 2 + popupBuyBonus.height * percent / 100;

    this.setPosition(popupBuyBonus, { x: parentWidth / 2, y: popupPositionY });

    this.configButtonsImage([
      acceptHover,
      acceptNormal,
      declineHover,
      declineNormal,
    ], { scale: .8 });

    /** Set Decline Container */
    this.setPosition(
      declineContainer,
      {
        x: spaceForButton + padding + actionWidth / 2,
        y: popupPositionY + 20,
      },
    );
    const declineRect = this.createGraphics()
      .drawRect(-actionWidth / 2, -actionHeight - 60, actionWidth, actionHeight)
      .endFill();
    declineRect.addChild(
      declineHover,
      declineNormal,
    );
    declineContainer.addChild(
      declineRect,
    );

    /** Set Accept Container */
    this.setPosition(
      acceptContainer,
      {
        x: parentWidth - (padding + actionWidth / 2 + spaceForButton),
        y: popupPositionY + 20,
      },
    );
    const acceptRect = this.createGraphics()
      .drawRect(-actionWidth / 2, -actionHeight - 60, actionWidth, actionHeight)
      .endFill();
    acceptRect.addChild(
      acceptHover,
      acceptNormal,
    );
    acceptContainer.addChild(
      acceptRect,
    );

    blackRect.interactive = true;
    blackRect.buttonMode = false;

    blackRect.on('pointerdown', (event) => {
      event.stopPropagation();
    });

    acceptRect.interactive = true;
    acceptRect.buttonMode = false;
    this.setupButtonEvents({ parent: acceptRect, hoverIcon: acceptHover, normalIcon: acceptNormal });

    declineRect.interactive = true;
    declineRect.buttonMode = false;
    this.setupButtonEvents({ parent: declineRect, hoverIcon: declineHover, normalIcon: declineNormal });

    declineRect.on('pointerdown', () => {
      soundService.playBuyBonusCloseClick();
      popupHelper.setBuyBonusPopupState(false);
    });

    acceptRect.on('pointerdown', () => {
      autoSpinHelper.stopAutoSpin();
      soundService.playBuyBonusBuyClick();
      popupHelper.setBuyBonusPopupState(false);
      onClick();
    });

    const popupTextTitle = new this.PIXI.Text(`${getTranslation(translationKeys.BUY_BONUS_GAME)}`, {
      fontFamily: FONT_LATO_NAME,
      fontSize: 33.5,
      fontWeight: 900,
      fill: 0xf7e0b4,
      align: 'center',
    });
    popupTextTitle.anchor.set(.5);

    const popupTextValue = new this.PIXI.Text('', {
      fontFamily: FONT_BALOO_NAME,
      fontSize: 81.5,
      fontWeight: 900,
      fill: 0xf7e0b4,
      align: 'center',
    });

    popupTextValue.anchor.set(.5);

    this.setPosition(popupTextTitle, { x: 0, y:  -80 * scale});
    this.setPosition(popupTextValue, { x: 0, y: 0 });

    this.addSubscriber(SPIN_AMOUNT_ACTIVE.subscribe(i => {
      const list = SPIN_AMOUNT.getValue();
      popupTextValue.text = `${amountFormatter.format(list[i] * BONUS_AMOUNT_MULTIPLE)} ${partnerService.displayCurrency}`;
      if (popupTextValue.text.length >= 13) {
        popupTextValue.style.fontSize = 78;
      }
      if (popupTextValue.text.length >= 14) {
        popupTextValue.style.fontSize = 72;
      }

      if (popupTextValue.text.length < 13) {
        popupTextValue.style.fontSize = 81.5;
      }
    }, true));

    popupTextTitle.scale.set(scale);
    popupTextValue.scale.set(scale);
    acceptContainer.scale.set(scale);
    declineContainer.scale.set(scale);

    const textsContainer = this.createContainer('textsContainer');
    this.setPosition(textsContainer, {x: parentWidth / 2, y: popupPositionY - 250 * scale})

    textsContainer.addChild(
      popupTextTitle,
      popupTextValue,
    )

    popupContainer.addChild(
      popupBuyBonus,
      textsContainer,
      declineContainer,
      acceptContainer,
    );

    container.addChild(
      blackRect,
      popupContainer,
      shadow,
    );
    return container;
  }
}
